import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  CardHeader,
  Dialog,
  Loading,
} from "../../../../../components";
import {
  PessoaService,
  history,
  OrigemMercadoriaService,
  notification,
  UnidadeComercialService,
  ProdutosService,
  OrdemServicoService,
  OperacaoFiscalService,
  ConfiguracoesOrdemServicoService,
} from "../../../../../services";
import { responseErros, formatDate, formatPrice } from "../../../../../utils";
import OrdemServicoStepperContent from "./ordem-servico-stepper-content.component";
import { NfSaida, NfSaidaItem } from "../../../entities";
import {
  calculaSubtotalParaChave,
  formatProdutosTotalizadoresDto,
  totalItensLista,
} from "../../../add-nf-saida/add-nf-saida";
import { value } from "../../../../../helpers";
import shortid from "shortid";

const ImportarOrdemServicoDialog = ({
  openImportarOrdemServicoDialog,
  setOpenImportarOrdemServicoDialog,
  filtrosDialog,
  setFiltrosDialog,
}) => {
  const ordemServicoService = new OrdemServicoService();
  const pessoaService = new PessoaService();
  const unidadeComercialService = new UnidadeComercialService();
  const origemMercadoriaService = new OrigemMercadoriaService();
  const produtosService = new ProdutosService();
  const operacaoFiscalService = new OperacaoFiscalService();
  const configuracoesOrdemServicoService =
    new ConfiguracoesOrdemServicoService();

  const [loading, setLoading] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [ordemServicos, setOrdemServicos] = useState([]);
  const [ordemServicosSelecionadas, setOrdemServicosSelecionadas] = useState(
    [],
  );
  const [ordemServicoSelectionModel, setOrdemServicoSelectionModel] = useState(
    [],
  );
  const [itemSelectionModel, setItemSelectionModel] = useState([]);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const [agruparProdutosIguais, setAgruparProdutosIguais] = useState(false);
  const [unidadeList, setUnidadeList] = useState([]);
  const [origensMercadoriaList, setOrigensMercadoriaList] = useState([]);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [listaOperacoesFiscais, setListaOperacoesFiscais] = useState([]);
  const [operacaoFiscal, setOperacaoFiscal] = useState(null);
  const [criterioOperacaoFiscal, setCriterioOperacaoFiscal] = useState(null);
  const [operacoesFiscaisCarregadas, setOperacoesFiscaisCarregadas] =
    useState(false);
  const steps = [
    "Selecionar Ordens de Serviço",
    "Selecionar Itens",
    "Selecionar Operação Fiscal",
  ];

  const colunas = [
    {
      field: "serieDocumento",
      headerName: "Série",
      align: "center",
      headerAlign: "center",
      width: 100,
      valueGetter: (params) => value.text(params.value),
    },
    {
      field: "numeroDocumento",
      headerName: "Nº Documento",
      align: "center",
      headerAlign: "center",
      flex: 100,
      filtrar: true,
    },
    {
      field: "clienteNome",
      headerName: "Cliente",
      width: 300,
      sortable: false,
      valueGetter: (params) =>
        params.value ??
        listaPessoas.find((item) => item.id == params.row.clienteId)
          ?.nomeRazaoSocial,
    },
    {
      field: "dataAbertura",
      headerName: "Data Abertura",
      align: "center",
      headerAlign: "center",
      flex: 100,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "dataPrevistaFechamento",
      headerName: "Previsão Fechamento",
      align: "center",
      headerAlign: "center",
      flex: 150,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "totalOrdemServico",
      headerName: "Valor",
      align: "right",
      headerAlign: "right",
      flex: 150,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value, false),
    },
  ];

  useEffect(() => {
    if (openImportarOrdemServicoDialog) {
      buscarOrdemServico();
      buscarUnidadesComerciais();
      buscarOrigensMercadoria();
      buscarOperacoesFiscaisImportacaoOrdemServico();
    }
  }, [openImportarOrdemServicoDialog]);

  const buscarOperacoesFiscaisImportacaoOrdemServico = async () => {
    setOperacoesFiscaisCarregadas(false);
    const resultConfiguracao = await configuracoesOrdemServicoService.getAll();
    if (resultConfiguracao.isAxiosError) {
      responseErros(resultConfiguracao);
      return;
    }
    const configuracao = resultConfiguracao.data?.[0];
    const criterio = {
      movimentacaoEstoque: !configuracao?.movimentacaoEstoque,
      movimentacaoFinanceira: !configuracao?.movimentacaoFinanceira,
    };
    setCriterioOperacaoFiscal(criterio);
    const filtro = {
      ativado: true,
      nonPaginated: true,
      tipoOperacao: "SAIDA",
      finalidade: ["NFE_NORMAL"],
      ...criterio,
      restritiva: true,
      porIntervalo: [],
    };
    const result = await operacaoFiscalService.getAllFiltroAvancado(filtro);
    if (!result.isAxiosError) {
      setListaOperacoesFiscais(result.data);
      setOperacoesFiscaisCarregadas(true);
    } else {
      responseErros(result);
    }
  };

  const buscarOrdemServico = async (filtro) => {
    setLoading(true);
    const filtros = {
      ...filtro,
      hasItens: true,
      status: "FECHADO",
      importacao: ["NAO_IMPORTADO", "IMPORTADO_PARCIAL"],
    };
    const result = await ordemServicoService.getAllFiltroAvancado(filtros);
    if (!result.isAxiosError) {
      setOrdemServicos(result.data.rows);
      buscarRelacionamentosOrdemServico(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarOrdemServicoAvancado = async (filtros) => {
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filtros,
      hasItens: true,
      status: "FECHADO",
      importacao: ["NAO_IMPORTADO", "IMPORTADO_PARCIAL"],
      restritiva: true,
    };
    if (filtrosAvancadosTemp.dataInicial && filtrosAvancadosTemp.dataFinal) {
      porIntervalo.push({
        coluna: "dataAbertura",
        de: filtrosAvancadosTemp.dataInicial,
        ate: filtrosAvancadosTemp.dataFinal,
      });
    }
    delete filtrosAvancadosTemp.dataInicial;
    delete filtrosAvancadosTemp.dataFinal;
    const result = await ordemServicoService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setOrdemServicos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarRelacionamentosOrdemServico = (listaOrdemServico) => {
    const idsPessoas = [];
    for (const objeto of listaOrdemServico) {
      if (objeto.clienteId) idsPessoas.push(objeto.clienteId);
    }
    buscarPessoas([...new Set(idsPessoas)]);
  };

  const buscarPessoas = (listaIds) => {
    if (listaIds.length > 0) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        disabledInclude: true,
        attributes: [
          "id",
          "nomeRazaoSocial",
          "email",
          "cnpjCpf",
          "inscricaoEstadual",
        ],
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          const listaPessoas = result.data;
          setListaPessoas(listaPessoas);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const handleDateChange = (date, fieldName) => {
    setFiltrosDialog((prevState) => ({
      ...prevState,
      [fieldName]: date != "Invalid Date" ? formatDate.toSend(date) : null,
    }));
  };

  const handleInputChange = (e) => {
    e.persist();
    setFiltrosDialog((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleImportarOrdemServico = async () => {
    if (!itemSelectionModel?.length)
      return notification.alertaGenericos(
        "Selecione ao menos um produto para importar",
      );
    if (!operacaoFiscal)
      return notification.alertaGenericos(
        "Selecione a operação fiscal de faturamento da ordem de serviço",
      );
    if (
      operacaoFiscal.modeloDocumento == "055" ||
      operacaoFiscal.modeloDocumento == "065"
    )
      return notification.alertaGenericos(
        "Operação fiscal sem regra de tributação associada.",
      );
    window.nfSaidaEntity = await createNfSaida(
      ordemServicosSelecionadas[0],
      ordemServicosSelecionadas,
      itensSelecionados,
    );
    setOpenImportarOrdemServicoDialog(false);
    resetarDadosImportacao();
    history.push("/faturamento/nf-saida/importar-ordem-servico");
  };

  const buscarOrigensMercadoria = async () => {
    origemMercadoriaService.getAll().then((res) => {
      if (!res.isAxiosError) {
        setOrigensMercadoriaList(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const buscarUnidadesComerciais = async () => {
    const filtros = {
      nonPaginated: true,
    };
    unidadeComercialService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setUnidadeList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const createNfSaida = async (ordemServico, ordemServicos, itens) => {
    const desconto = calculaSubtotalParaChave(itens, "", "desconto");
    const acrescimo = calculaSubtotalParaChave(itens, "", "acrescimo");
    const despesas = calculaSubtotalParaChave(itens, "", "despesas");
    const itensTemp = await buscarDadosProdutos(
      formatProdutosTotalizadoresDto(itens, totalItensLista(itens), {
        ...ordemServico,
        desconto,
        acrescimo,
        despesas,
      }),
    );
    let itensParaProcessar = itensTemp.filter(
      (item) => itemSelectionModel.indexOf(item.id) > -1,
    );

    if (agruparProdutosIguais) {
      itensParaProcessar = agruparItensPorProduto(itensParaProcessar);
    }
    const nfSaidaItens = itensParaProcessar.map((item, index) => {
      const nfSaidaItem = new NfSaidaItem(
        item,
        index,
        operacaoFiscal,
        origensMercadoriaList,
        unidadeList,
      );
      const valorUnit = parseFloat(Number(item.valorUnitario).toFixed(2));
      const quantidade = Number(item.saldoExportar) || 0;
      return {
        ...nfSaidaItem,
        produto: item.produto,
        valor: valorUnit,
        quantidade,
        valorUnitario: valorUnit,
        subtotal: parseFloat((valorUnit * quantidade).toFixed(2)),
      };
    });
    const informacoesAdicionais = ordemServicos
      .map((os) => {
        return `Importado da ordem de serviço ${os.serieDocumento ?? ""}/${
          os.numeroDocumento ?? ""
        }`;
      })
      .join("\n");
    let cliente = {};
    if (ordemServico.clienteId) {
      cliente = listaPessoas.find((item) => item.id == ordemServico.clienteId);
    }
    const dadosNfSaida = {
      dataEmissao: formatDate.toSend(new Date()),
      modalidadeFrete: "mfSemFrete",
      freteCompoeNota: false,
      ordemServicoImport: true,
      clienteId: ordemServico?.clienteId,
      clienteEmail: cliente?.email,
      clienteCnpjCpf: cliente?.cnpjCpf,
      clienteNome: cliente?.nomeRazaoSocial,
      clienteInscricaoEstadual: cliente?.inscricaoEstadual ?? undefined,
      desconto: desconto ?? 0,
      acrescimo: acrescimo ?? 0,
      despesas: despesas ?? 0,
      ordemServicoIds: ordemServicos.map((o) => o.id),
      operacaoFiscalId: operacaoFiscal.id,
      operacaoFiscal,
      serieDocumento: operacaoFiscal?.serieDocumento,
      modeloDocumento: operacaoFiscal?.modeloDocumento,
      informacoesAdicionais,
      nfSaidaItens,
    };
    return new NfSaida(dadosNfSaida, true);
  };

  function criarItemAgrupado(item, quantidade, subtotal) {
    return {
      ...item,
      saldoExportar: quantidade,
      _subtotalTemp: subtotal,
      _originais: [item.id],
    };
  }

  function agruparItensPorProduto(itens) {
    const map = new Map();

    itens.forEach((item) => {
      const key = item.produtoId;
      const quantidade = Number(item.saldoExportar) || 0;
      const valorUnit = Number(item.valorUnitario) || 0;
      const subtotal = valorUnit * quantidade;

      if (!map.has(key)) {
        map.set(key, criarItemAgrupado(item, quantidade, subtotal));
        return;
      }

      const acc = map.get(key);

      acc.saldoExportar += quantidade;
      acc._subtotalTemp += subtotal;
      acc._originais.push(item.id);

      map.set(key, acc);
    });

    return Array.from(map.values()).map(calcularValoresAgrupados);
  }

  function calcularValoresAgrupados(item) {
    const totalQtd = Number(item.saldoExportar) || 0;
    const subtotalTotal = Number(item._subtotalTemp) || 0;

    const valorUnitario =
      totalQtd > 0 ? subtotalTotal / totalQtd : Number(item.valorUnitario) || 0;

    return {
      ...item,
      id: shortid.generate(),
      saldoExportar: totalQtd,
      valorUnitario,
    };
  }

  async function buscarDadosProdutos(itens) {
    const listaIds = itens.map(({ produtoId }) => produtoId);
    const res = await produtosService.getAll({
      id: listaIds,
      nonPaginated: true,
    });
    if (!res.isAxiosError) {
      const produtosMap = new Map(
        res.data.map((produto) => [produto.id, produto]),
      );
      return itens.map((item) => ({
        ...item,
        ...produtosMap.get(item.produtoId),
        id: item.id,
      }));
    } else {
      responseErros(res);
    }
  }

  const resetarDadosImportacao = () => {
    setFiltrosDialog({});
    setOrdemServicosSelecionadas([]);
    setOperacaoFiscal(null);
    setActiveStep(0);
  };

  const handleCancelarImportacao = () => {
    resetarDadosImportacao();
    setOpenImportarOrdemServicoDialog(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      if (props.filtros?.status) {
        delete props.filtros.status;
      }
      buscarOrdemServico(props.filtros);
    } else {
      buscarOrdemServicoAvancado(props.filtros);
    }
  };

  return (
    <Dialog fullWidth maxWidth="xl" open={openImportarOrdemServicoDialog}>
      <Box sx={{ m: 2 }}>
        <CardHeader title="Importar Itens da O.S" />
      </Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <OrdemServicoStepperContent
        sendServer={sendServerDatagrid}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        handleImportarOrcamento={handleImportarOrdemServico}
        handleCancelarImportacao={handleCancelarImportacao}
        handleDateChange={handleDateChange}
        handleInputChange={handleInputChange}
        filtrosDialog={filtrosDialog}
        setFiltrosDialog={setFiltrosDialog}
        colunas={colunas}
        ordemServicos={ordemServicos}
        ordemServicosSelecionadas={ordemServicosSelecionadas}
        setOrdemServicosSelecionadas={setOrdemServicosSelecionadas}
        itensSelecionados={itensSelecionados}
        setItensSelecionados={setItensSelecionados}
        buscarOrdemServicoAvancado={buscarOrdemServicoAvancado}
        listaClientes={listaPessoas}
        itemSelectionModel={itemSelectionModel}
        setItemSelectionModel={setItemSelectionModel}
        ordemServicoSelectionModel={ordemServicoSelectionModel}
        setOrdemServicoSelectionModel={setOrdemServicoSelectionModel}
        agruparProdutosIguais={agruparProdutosIguais}
        setAgruparProdutosIguais={setAgruparProdutosIguais}
        operacaoFiscal={operacaoFiscal}
        setOperacaoFiscal={setOperacaoFiscal}
        listaOperacoesFiscais={listaOperacoesFiscais}
        criterioOperacaoFiscal={criterioOperacaoFiscal}
        operacoesFiscaisCarregadas={operacoesFiscaisCarregadas}
      />
      <Loading loading={loading} />
    </Dialog>
  );
};

export default ImportarOrdemServicoDialog;
